/**
 * What needs her today (D-877 to D-883).
 *
 * One read, because the alternative is five screens she has to remember to
 * open. Every row the server sends is a row she holds the capability for and
 * which has something in it — the filtering and the hiding both happen there,
 * so this file renders what it is given and decides nothing.
 */

export type DashboardRow = {
  /** `attendance` · `asks` · `standing` · `feedback` · `feed`. */
  key: string
  /**
   * How many, or **null when the row could not be loaded**.
   *
   * ⚠ Null rather than 0 on purpose. D-882 hides a row counting zero, and the
   * obvious implementation of that rule filters on the count — which would
   * delete exactly the row that exists to say a source is broken (D-209).
   * Branch on `failed` first, always.
   */
  count: number | null
  /** What the number is about, in the studio's words. Never empty. */
  about: string
  /** The source refused. The count is null and the row still shows. */
  failed: boolean
}

/** One class on tonight, as the approved deck draws it: time, name, room. */
export type StudioTonight = {
  occurrenceId: string
  className: string
  startTime: string | null
  room: string | null
  /** The studio's own colour for that room, so the list reads the way it does. */
  roomColor: string | null
  teachers: string[]
}

/**
 * Context around the tiles, not a tile.
 *
 * **Never hidden and never arranged** — it says what kind of evening it is
 * rather than asking her to do something, which is the distinction D-882 draws
 * by hiding tiles that count nothing. `null` means the week could not be read,
 * and the screen simply goes without it rather than failing.
 */
export type StudioToday = {
  classesThisWeek: number
  tonight: StudioTonight[]
}

/**
 * One named thing waiting on her (the deck's *What needs your attention*).
 *
 * **A tile says two; this says who.** `key` is the tile it belongs to, so the
 * item can carry the same colour as the figure it is an example of.
 */
export type DashboardItem = {
  key: string
  /** `critical` · `warning` · `normal`. Anything else renders muted. */
  severity: string
  title: string
  detail: string | null
  href: string | null
}

export type Dashboard = {
  /** The studio's calendar date (D-68), not the browser's. */
  today: string
  /** Ranked: critical first, then her own tile order. Capped at eight. */
  attention: DashboardItem[]
  /** Null when the week could not be read. The tiles are unaffected. */
  studio: StudioToday | null
  /**
   * Only rows that need her.
   *
   * **Empty is the good day, not a failure.** A row counting zero is gone
   * before it gets here (D-882), so an empty list means the studio is in order
   * and the screen has to say so — never render it as a page that failed to
   * load.
   */
  rows: DashboardRow[]
}

export async function fetchDashboard(): Promise<Dashboard> {
  const res = await fetch('/api/dashboard')
  if (!res.ok) throw new Error(`Could not load what needs you (${res.status})`)
  return res.json()
}

/**
 * The heading a row reads under.
 *
 * **Ours to write, so a literal** (D-678): this is inside the signed-in app,
 * where buttons, labels and column headings are ours to maintain and a studio
 * owner does not want to rename them. Her words are rows only on the public
 * site and in anything the app sends.
 */
const TITLES: Record<string, string> = {
  attendance: 'Missing attendance',
  asks: 'Requests',
  standing: 'Dancers below their mark',
  feedback: 'Feedback',
  messages: 'Waiting on a reply',
  carryover: 'To re-enter in Jackrabbit',
  feed: 'The Jackrabbit feed',
}

export function titleOf(key: string): string {
  // A row the server knows and this file does not still reads as something.
  return TITLES[key] ?? key
}

/** Afternoon runs to 16:59, because a 4:30 class starts in the afternoon (D-885). */
export function greeting(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

/**
 * The reader's **own** hour, never the server's (D-885).
 *
 * Takes the instant so a test can hand it one — `minutesNow` in `api/dancers.ts`
 * is the same shape for the same reason, and a zero-argument version shipped
 * green under `getUTCHours()`, which is the D-68 mistake this rule exists to
 * refuse.
 */
export function greetingNow(at: Date = new Date()): string {
  return greeting(at.getHours())
}

/**
 * Where a row sends her.
 *
 * **A row that counts something she cannot act on is worse than no row**, so
 * every one of these is a screen that can clear it. `null` means the row is
 * informational and must not render as a link — a link that goes nowhere reads
 * as broken, and a link to a screen that refuses her is worse (the rule D-209's
 * neighbours keep recording).
 */
const ROUTES: Record<string, string | null> = {
  // `/attendance` is the *standing* view and `/attendance/missing` is the
  // register queue — checked against `App.tsx`, because a first draft had them
  // the other way round and both routes exist, so nothing would have failed.
  attendance: '/attendance/missing',
  asks: '/asks',
  standing: '/attendance',
  feedback: '/admin/feedback',
  messages: '/messages',
  carryover: '/admin/carryover',
  feed: '/admin/import/activity',
}

export function routeOf(key: string): string | null {
  return ROUTES[key] ?? null
}

/**
 * The colour down a tile's left edge.
 *
 * **One hue per tile, so she recognises it before reading it** — `asked`,
 * 2026-10-07: *"give each one a unique color"*. The colour identifies the tile
 * rather than grading its severity: a tile is on the screen at all only because
 * it has something in it (D-882), so *how bad is this* is the number's job and
 * *which one is this* is the colour's.
 *
 * ⚠ **Hex values rather than Tailwind classes**, because the rail is drawn
 * through `--tile-color` and `.tile-tint` derives a readable value per theme —
 * the same device `.room-tint` uses for a studio-chosen room colour. A stored
 * hue cannot know which theme it will be shown in, and `#0b8a5f` on the dark
 * ground is the mistake that device exists to prevent.
 *
 * They are the app's own semantic tokens where one fits, so the amber on
 * *missing attendance* is the amber everything else warns in.
 */
const TINTS: Record<string, string> = {
  attendance: '#b26a05',
  asks: '#d6336c',
  standing: '#c42f2f',
  feedback: '#0b8a5f',
  messages: '#3f72a8',
  carryover: '#7e5ba6',
  feed: '#0f766e',
}

/** The muted default is what an unrecognised row gets, rather than no rail. */
export function tintOf(key: string): string {
  return TINTS[key] ?? '#86767e'
}

/**
 * Every tile that exists, whether or not it has anything in it today.
 *
 * **Arranging needs the whole set and the dashboard needs the live one.** A
 * tile counting zero never arrives in `rows` (D-882), so a drag list built from
 * what is on screen could never position the tile that is quiet this week — she
 * would be arranging a different list each time she opened it.
 */
export type ArrangeableTile = {
  key: string
  /** Null when the tile has nothing in it today, so the card can say so. */
  count: number | null
  hidden: boolean
}

export async function fetchArrangement(): Promise<ArrangeableTile[]> {
  const res = await fetch('/api/dashboard/arrange')
  if (!res.ok) throw new Error(`Could not load your tiles (${res.status})`)
  return res.json()
}

export async function saveArrangement(tiles: ArrangeableTile[]): Promise<void> {
  const res = await fetch('/api/dashboard/order', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      shown: tiles.filter(t => !t.hidden).map(t => t.key),
      hidden: tiles.filter(t => t.hidden).map(t => t.key),
    }),
  })

  if (!res.ok) throw new Error(`That did not save (${res.status})`)
}
